import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import PackageInfo from '../../package.json';
import {NavigationEnd, Router} from '@angular/router';
import {filter, skip, takeUntil, tap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {MatDrawer} from '@angular/material/sidenav';
import {environment} from '../environments/environment';
import {ShortcutInput} from 'ng-keyboard-shortcuts';
import {AppUpdateService} from './core/app-update.service';
import {ShowMenuInitializerService} from './shared/show-menu-initializer.service';
import {WhatsNewContentService} from "./whats-new/whats-new-content.service";
import {AuthenticationService} from "./core/authentication.service";
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import moment from "moment-timezone";


export const PROGRESS_ID = 'progress-bar';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private showLoginMenuRoutes = [
    '/login',
    '/reset-password',
    '/force-change-password',
  ];

  public readonly PROGRESS_ID = PROGRESS_ID;
  public readonly VERSION: string = PackageInfo.version;
  public readonly CURRENT_YEAR: number = new Date().getFullYear();

  public readonly showNotProductionBanner = environment.showNotProductionBanner;
  public readonly notProductionBannerText = environment.notProductionBannerText;

  private destroyed$: Subject<void> = new Subject<void>();

  @ViewChild('drawer', { static: true }) public readonly drawer: MatDrawer;

  shortcuts: ShortcutInput[] = [];

  hasWhatsNewData = false;
  showWhatsNewIndicator = false;
  showWhatsNewIndicatorMenu = false;

  constructor(
    public router: Router,
    private translate: TranslateService,
    public appUpdateService: AppUpdateService,
    public showMenuInitializerService: ShowMenuInitializerService,
    private whatsNewContentService: WhatsNewContentService,
    public authenticationService: AuthenticationService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    public changeDetector: ChangeDetectorRef,

  ) {
  }

  ngOnInit(): void {
    this.initializeTranslations();
    this.hookCloseSidenavOnNavigation();

    this.authenticationService.languageInfo$
      .subscribe(() => {
        this.reloadWhatsNewData();
      });

    this.authenticationService.activeOrganizationInfo$
      .pipe(
        skip(1),
      )
      .subscribe(() => {
        this.reloadWhatsNewData();
      });

    this.matIconRegistry.addSvgIcon(
      "bellBadge",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/icons/bellBadge.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "bell",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/icons/bell.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "bellWhiteBadge",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/icons/bellWhiteBadge.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "bellBlack",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/icons/bellBlack.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "bellBlackBadge",
      this.domSanitizer.bypassSecurityTrustResourceUrl("assets/icons/bellBlackBadge.svg")
    );
  }

  private reloadWhatsNewData() {
    this.showWhatsNewIndicator = false;
    this.showWhatsNewIndicatorMenu = false;
    this.hasWhatsNewData = false;

    this.whatsNewContentService.reloadLatestContent().subscribe((contents) => {
      this.showWhatsNewIndicator = contents.latestVersionNeedsHighlighting;
      this.showWhatsNewIndicatorMenu = contents.latestVersionNeedsHighlighting;
      if (contents.contents.length != 0) {
        this.hasWhatsNewData = true;
      }
      this.changeDetector.detectChanges();
    });
  }

  private initializeTranslations() {
    this.translate.setDefaultLang('en');
    this.translate.use(localStorage.getItem('language') || 'en');
    moment.locale(localStorage.getItem('language') || 'en')
  }

  private hookCloseSidenavOnNavigation(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => this.drawer?.close()),
      takeUntil(this.destroyed$),
    ).subscribe();
  }

  showLoginFooter() {
    return this.atLeastOneTrueHelper(this.showLoginMenuRoutes);
  }

  atLeastOneTrueHelper(arr: Array<string>) {
    return arr.some((item) => {
      return this.router.url.startsWith(item);
    });
  }

  ngAfterViewInit() {
    this.shortcuts.push(
      {
        key: ['u s e r s'],
        label: 'Navigation',
        description: 'Go to the users list',
        command: () => this.router.navigate(['/users']),
      },
      {
        key: ['s t o r e s'],
        label: 'Navigation',
        description: 'Go to the stores list',
        command: () => this.router.navigate(['/stores']),
      },
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onWhatsNewSectionOpened() {
    this.showWhatsNewIndicator = false;
    this.showWhatsNewIndicatorMenu = false;
  }

}
